import { daysUntilReset, monthLabel } from '../budget/engine'
import { deriveActions, deriveBudget, deriveItems } from '../budget/repo'
import { allProgress, formatKg } from '../exercise/exercise'
import { formatMeasurement, formatPct, trackerView } from '../health/health'
import { formatKcal, summarise } from '../nutrition/nutrition'
import { formatMoney } from '../lib/money'
import { navigate } from '../lib/router'
import { sortedRecipes } from '../cookbook/cookbook'
import { savingsView } from '../savings/savings'
import { useStore } from '../store'
import { groupByDay, isDone, visibleTodos } from '../todo/todo'
import { Bar } from '../ui/parts'
import { isHidden } from './sections'

/**
 * A scrolling column of flat cards. Each carries the number you would otherwise open the
 * section to read, so the dashboard is worth looking at rather than tapping through.
 */
export function Dashboard() {
  const { snapshot } = useStore()
  if (!snapshot) return null

  const budget = deriveBudget(snapshot)
  const items = deriveItems(snapshot)
  const actions = deriveActions(snapshot)
  const nutrition = summarise(snapshot.nutrition)
  const weight = trackerView(snapshot.health, 'weight')
  const booty = trackerView(snapshot.health, 'booty')
  const exercises = allProgress(snapshot.exercise, 'all').filter((e) => e.targetKg !== null)
  const todos = visibleTodos(snapshot.todos)
  const now = new Date()
  const open = todos.filter((t) => !isDone(t, now))
  const wishlist = items.filter((i) => i.status === 'wishlist').length
  const savings = savingsView(snapshot.savings)
  const recipes = sortedRecipes(snapshot.cookbook)

  // One list decides what the dashboard shows, the bottom bar shows and the drawer shows,
  // so a section hidden in settings cannot linger in one of them.
  const hidden = snapshot.app.hiddenSections
  const shows = (id: Parameters<typeof isHidden>[1]) => !isHidden(hidden, id)

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <div className="screen">
      <div className="topbar">
        <div className="grow">
          <h1>Dashboard</h1>
          <p className="sub">{today}</p>
        </div>
      </div>

      <div className="stack">
        {shows('budget') ? (
        <button type="button" className="app-card" onClick={() => navigate('/budget')}>
          <div className="row between">
            <span className="lbl">Budget available</span>
            <span className="lbl">{monthLabel(budget.currentMonth)}</span>
          </div>
          <div className={`big ${budget.availableNowFils < 0 ? 'over' : ''}`}>
            {formatMoney(budget.availableNowFils)}
            <span style={{ fontSize: 15, fontWeight: 500, marginLeft: 6 }}>AED</span>
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            {wishlist} on the list · {formatMoney(budget.committedFutureFils)} committed ahead ·
            resets in {daysUntilReset()} days
          </div>
          {snapshot.settings.earnEnabled && actions.length > 0 ? (
            <div style={{ fontSize: 13, marginTop: 6, color: 'var(--primary)' }}>
              {actions.length} action{actions.length === 1 ? '' : 's'} available to earn extra
            </div>
          ) : null}
        </button>
        ) : null}

        {shows('nutrition') ? (
        <button type="button" className="app-card" onClick={() => navigate('/nutrition')}>
          <div className="row between">
            <span className="lbl">Calories remaining</span>
            <span className="lbl">Today</span>
          </div>
          <div className="row between" style={{ alignItems: 'flex-end' }}>
            <span className={`big ${nutrition.caloriesRemaining < 0 ? 'over' : ''}`}>
              {Math.round(nutrition.caloriesRemaining)}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              {formatKcal(nutrition.totals.calories)} of {formatKcal(nutrition.targets.calories)}
            </span>
          </div>

          {/* Three slim bars on one row: enough to see whether a macro is short, without
              taking over the card. */}
          <div className="macro-compact">
            {nutrition.macros.map((macro) => (
              <div key={macro.key}>
                <div className="macro-compact-top">
                  <span>{macro.label[0]}</span>
                  <span className={`tnum ${macro.over ? 'over' : ''}`}>
                    {Math.round(Math.abs(macro.remainingG))}
                    {macro.over ? ' over' : ' left'}
                  </span>
                </div>
                <div className="track" style={{ height: 6, margin: '4px 0 0' }}>
                  <span
                    style={{
                      width: `${macro.fraction * 100}%`,
                      background: macro.over
                        ? 'var(--rec-low)'
                        : macro.key === 'protein'
                          ? 'var(--protein)'
                          : macro.key === 'carbs'
                            ? 'var(--carbs)'
                            : 'var(--fat)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </button>
        ) : null}

        {shows('health') ? (
        <button type="button" className="app-card" onClick={() => navigate('/health')}>
          <div className="lbl">Body</div>
          <div className="row" style={{ gap: 20, marginTop: 4 }}>
            <MiniTracker
              label="Weight"
              value={formatMeasurement(weight.current, weight.unit)}
              goal={weight.goal === null ? null : formatMeasurement(weight.goal, weight.unit)}
              change={formatPct(weight.changePct)}
              hasChange={weight.changePct !== null}
            />
            <MiniTracker
              label="Booty"
              value={formatMeasurement(booty.current, booty.unit)}
              goal={booty.goal === null ? null : formatMeasurement(booty.goal, booty.unit)}
              change={formatPct(booty.changePct)}
              hasChange={booty.changePct !== null}
            />
          </div>
        </button>
        ) : null}

        {shows('todo') ? (
        <button type="button" className="app-card" onClick={() => navigate('/todo')}>
          <div className="row between">
            <span className="lbl">To-do</span>
            <span className="lbl">{open.length} open</span>
          </div>
          {open.length === 0 ? (
            <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
              Nothing scheduled.
            </div>
          ) : (
            groupByDay(open)
              .slice(0, 2)
              .map((group) => (
                <div key={group.date} style={{ marginTop: 6 }}>
                  <div className="lbl">{group.label}</div>
                  {group.items.slice(0, 3).map((todo) => (
                    <div key={todo.id} style={{ fontSize: 14 }}>
                      {todo.time ? (
                        <span className="tnum muted" style={{ marginRight: 8 }}>
                          {todo.time}
                        </span>
                      ) : null}
                      {todo.title}
                    </div>
                  ))}
                </div>
              ))
          )}
        </button>
        ) : null}

        {shows('savings') && (savings.targetFils > 0 || savings.currentFils > 0) ? (
          <button type="button" className="app-card" onClick={() => navigate('/savings')}>
            <div className="row between">
              <span className="lbl">Savings</span>
              <span className="lbl">
                {savings.targetFils === 0 ? 'No goal' : `${Math.round(savings.fraction * 100)}%`}
              </span>
            </div>
            <div className="big">
              {formatMoney(savings.currentFils)}
              <span style={{ fontSize: 15, fontWeight: 500, marginLeft: 6 }}>AED</span>
            </div>
            {savings.targetFils > 0 ? (
              <>
                <Bar fraction={savings.fraction} />
                <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                  {savings.reached
                    ? 'Goal reached'
                    : `${formatMoney(savings.remainingFils)} to go`}
                </div>
              </>
            ) : null}
          </button>
        ) : null}

        {shows('cookbook') && recipes.length > 0 ? (
          <button type="button" className="app-card" onClick={() => navigate('/cookbook')}>
            <div className="row between">
              <span className="lbl">Cookbook</span>
              <span className="lbl">
                {recipes.length} recipe{recipes.length === 1 ? '' : 's'}
              </span>
            </div>
            {/* The three most recent, as photos: a cookbook you cannot see is a list. */}
            <div className="recipe-strip">
              {recipes.slice(0, 3).map((recipe) => (
                <div className="recipe-thumb" key={recipe.id}>
                  {recipe.imageUrl ? (
                    <img
                      src={recipe.imageUrl}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : null}
                  <span>{recipe.title}</span>
                </div>
              ))}
            </div>
          </button>
        ) : null}

        {shows('exercise') && exercises.length > 0 ? (
          <button type="button" className="app-card" onClick={() => navigate('/exercise')}>
            <div className="row between">
              <span className="lbl">Next lifts</span>
              <span className="lbl">Target</span>
            </div>
            {exercises.slice(0, 4).map((item) => (
              <div className="row between" key={item.exercise.id} style={{ fontSize: 14, marginTop: 4 }}>
                <span>{item.exercise.title}</span>
                <span className="tnum" style={{ fontWeight: 600 }}>
                  {formatKg(item.targetKg)}
                </span>
              </div>
            ))}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function MiniTracker({
  label,
  value,
  goal,
  change,
  hasChange,
}: {
  label: string
  value: string
  goal: string | null
  change: string
  hasChange: boolean
}) {
  return (
    <div>
      <div className="lbl">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {goal ? `Goal ${goal}` : 'No goal'}
        {hasChange ? ` · ${change}` : ''}
      </div>
    </div>
  )
}
